import { useToast } from '@components/Common/Toast/ToastProvider';
import { exportSubmissionsWorkbook } from '@helpers/exportSubmissionsWorkbook';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ActionIconButton from '@components/Common/ActionIconButton';
import { Stack } from '@mui/material';
import { fetchDashboardStatsRequest } from '@store/features/dashboard/actions';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@store/rootReducer';

/**
 * Overview's header actions.
 *
 * They used to sit inside Dashboard's own hero. Now that every tab shares one
 * hero owned by the parent, these live on their own so the parent can slot
 * them in without Dashboard having to lift its state.
 */
const DashboardActions: React.FC<{ superAdmin: boolean }> = ({ superAdmin }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [exporting, setExporting] = useState(false);
  const statsLoading = useSelector((s: RootState) => s.dashboard?.loading ?? false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const truncated = await exportSubmissionsWorkbook();
      // A short workbook is the one people act on without noticing. The file carries a Coverage
      // sheet, but the toast is what reaches someone who downloaded it and moved on.
      if (truncated.length) {
        showToast(
          'Workbook downloaded, but it is incomplete. See the Coverage sheet.',
          'warning',
        );
      } else {
        showToast('Submissions workbook downloaded', 'success');
      }
    } catch (err) {
      console.error('Failed to export submissions workbook', err);
      showToast('Could not generate the workbook. Please try again.', 'error');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      {superAdmin && (
        <ActionIconButton
          label="Download submissions workbook"
          icon={<FileDownloadOutlinedIcon />}
          onClick={handleExport}
          busy={exporting}
        />
      )}
      <ActionIconButton
        label="Refresh"
        color="primary"
        icon={<RefreshIcon />}
        onClick={() => dispatch(fetchDashboardStatsRequest())}
        busy={statsLoading}
      />
    </Stack>
  );
};

export default DashboardActions;
